class LeadSlideshow {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.lead-slideshow__slide');
    this.indicators = container.querySelectorAll('.lead-slideshow__indicator');
    this.prevButton = container.querySelector('.lead-slideshow__arrow--prev');
    this.nextButton = container.querySelector('.lead-slideshow__arrow--next');
    this.track = container.querySelector('.lead-slideshow__track');
    this.isTransitionSlide = container.classList.contains('lead-slideshow--transition-slide');
    
    this.currentIndex = 0;
    this.autoplayEnabled = container.getAttribute('data-autoplay') === 'true';
    this.speed = parseInt(container.getAttribute('data-speed'), 10) || 5000;
    
    this.isPaused = false;
    this.animationFrameId = null;
    this.startTime = null;
    this.elapsedTime = 0;
    
    // Swipe support variables
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }

  init() {
    if (this.slides.length <= 1) return;

    // Attach reference for Shopify Theme Editor events
    this.container.__slideshowInstance = this;

    // Event Listeners
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.prevSlide();
        this.resetTimer();
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.nextSlide();
        this.resetTimer();
      });
    }

    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        this.goToSlide(index);
        this.resetTimer();
      });
    });

    // Hover to pause
    this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.container.addEventListener('mouseleave', () => this.resumeAutoplay());

    // Swipe support on mobile
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });

    // Mark as active initially
    this.updateDom();
    
    // Start Autoplay Loop
    if (this.autoplayEnabled) {
      this.container.classList.add('is-playing');
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.isPaused = false;
    this.startTime = null;
    this.elapsedTime = 0;
    
    const tick = (timestamp) => {
      if (this.isPaused) return;

      if (!this.startTime) {
        this.startTime = timestamp - this.elapsedTime;
      }

      this.elapsedTime = timestamp - this.startTime;
      const progress = Math.min(this.elapsedTime / this.speed, 1);
      
      this.updateProgress(progress);

      if (this.elapsedTime >= this.speed) {
        this.nextSlide();
        this.startTime = null;
        this.elapsedTime = 0;
      }

      this.animationFrameId = requestAnimationFrame(tick);
    };

    this.animationFrameId = requestAnimationFrame(tick);
  }

  pauseAutoplay() {
    if (!this.autoplayEnabled) return;
    this.isPaused = true;
    this.container.classList.add('is-paused');
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  resumeAutoplay() {
    if (!this.autoplayEnabled) return;
    this.isPaused = false;
    this.container.classList.remove('is-paused');
    this.startAutoplay();
  }

  resetTimer() {
    if (!this.autoplayEnabled) return;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.resetProgressBars();
    this.startAutoplay();
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  goToSlide(index) {
    if (index === this.currentIndex) return;
    this.resetProgressBars();
    this.currentIndex = index;
    this.startTime = null;
    this.elapsedTime = 0;
    this.updateDom();
  }

  updateDom() {
    // Update slides classes
    this.slides.forEach((slide, idx) => {
      if (idx === this.currentIndex) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });

    // Update track position for slide transition style
    if (this.isTransitionSlide) {
      this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;
    }

    // Update indicators classes & aria-current attributes
    this.indicators.forEach((indicator, idx) => {
      if (idx === this.currentIndex) {
        indicator.classList.add('is-active');
        indicator.setAttribute('aria-current', 'true');
      } else {
        indicator.classList.remove('is-active');
        indicator.setAttribute('aria-current', 'false');
      }
    });
  }

  updateProgress(progress) {
    const activeIndicator = this.indicators[this.currentIndex];
    if (activeIndicator) {
      const progressBar = activeIndicator.querySelector('.lead-slideshow__indicator-progress-bar');
      if (progressBar) {
        progressBar.style.width = `${progress * 100}%`;
      }
    }
  }

  resetProgressBars() {
    this.indicators.forEach(indicator => {
      const progressBar = indicator.querySelector('.lead-slideshow__indicator-progress-bar');
      if (progressBar) {
        progressBar.style.width = '0%';
      }
    });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    if (this.touchEndX < this.touchStartX - swipeThreshold) {
      this.nextSlide();
      this.resetTimer();
    } else if (this.touchEndX > this.touchStartX + swipeThreshold) {
      this.prevSlide();
      this.resetTimer();
    }
  }
}

// Initialize all lead slideshows on window load to optimize initial page loading and prevent CLS
window.addEventListener('load', () => {
  const slideshowElements = document.querySelectorAll('.lead-slideshow');
  slideshowElements.forEach(element => {
    new LeadSlideshow(element);
  });
});

// Shopify Theme Editor Integration
document.addEventListener('shopify:section:select', (event) => {
  const sectionId = event.detail.sectionId;
  const slideElement = document.getElementById(`LeadSlideshow-${sectionId}`);
  if (slideElement && slideElement.__slideshowInstance) {
    slideElement.__slideshowInstance.pauseAutoplay();
  }
});

document.addEventListener('shopify:section:deselect', (event) => {
  const sectionId = event.detail.sectionId;
  const slideElement = document.getElementById(`LeadSlideshow-${sectionId}`);
  if (slideElement && slideElement.__slideshowInstance) {
    slideElement.__slideshowInstance.resumeAutoplay();
  }
});

document.addEventListener('shopify:block:select', (event) => {
  const sectionContainer = event.target.closest('.lead-slideshow');
  if (sectionContainer && sectionContainer.__slideshowInstance) {
    const slideIndex = parseInt(event.target.getAttribute('data-index'), 10);
    if (!isNaN(slideIndex)) {
      sectionContainer.__slideshowInstance.goToSlide(slideIndex);
      sectionContainer.__slideshowInstance.pauseAutoplay();
    }
  }
});

document.addEventListener('shopify:block:deselect', (event) => {
  const sectionContainer = event.target.closest('.lead-slideshow');
  if (sectionContainer && sectionContainer.__slideshowInstance) {
    sectionContainer.__slideshowInstance.resumeAutoplay();
  }
});
